import _ from "lodash";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { selectAccounts, selectAllFinancialCategories, selectFinancialCategories, selectSpenders } from "../store/userDataSlice";

export type UserSettingOption = {
    value: string;
    label: string;
    category?: string; // Optional category for income/expense/bill/saving/subscription/debt or account type CARD/CASH/SAVINGS/CHECKING
};

/**
 * Custom hook to retrieve user setting options for financial categories, accounts, and spenders.
 * 
 * @returns An array of user setting options categorized into:
 * 1. Financial categories (income, expense, bill, saving, subscription, debt)
 * 2. Accounts
 * 3. Spenders
 * 
 * Each option is an object with `value` and `label` properties, and optionally a `category` for financial categories.
 */
export default function useUserSettingOptions(): Array<UserSettingOption[]> {
    const categories = useSelector(selectAllFinancialCategories);
    const accounts = useSelector(selectAccounts);
    const spenders = useSelector(selectSpenders);

    return useMemo(() => {
        const categoryOptions = (categories || []).map((category) => ({
            value: category.id,
            label: category.name,
            category: category.category,
        }));

        return [
            _.uniqBy(categoryOptions, 'label'),
            accounts?.map((account) => ({
                value: account.id,
                label: account.name,
                category: account.type,
            })) || [],
            spenders?.map((spender) => ({
                value: spender.id,
                label: spender.name,
            })) || [],
        ];
    }, [categories, accounts, spenders]);
}